import type { Session } from '@supabase/supabase-js';
import type { PaymentIncident } from '@nuberu/types';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@nuberu/ui';
import { useCallback, useEffect, useId, useState, type ReactElement } from 'react';

import { ApiError } from '@/lib/api';

import {
  actOnIncident,
  fetchPaymentIncidents,
  resolveIncident,
  type IncidentAction,
} from './fetch-payments';
import {
  actionsFor,
  formatMoney,
  formatWhen,
  fulfilHint,
  reasonLabel,
  resolutionLabel,
  subjectLabel,
} from './format';

// "Pagos sin reserva": customers charged without their booking, bono or
// rental, and refunds that gave up. Nuberu resolves them here — create what
// was paid for, refund it, or close it with a note — and every action is
// recorded on the incident under the admin's email. Each action asks for a
// second click to confirm: these move real money.

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; open: PaymentIncident[]; resolved: PaymentIncident[] };

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof ApiError
    ? err.status === 403
      ? 'Tu cuenta no tiene acceso al backoffice.'
      : err.message
    : fallback;

const ACTION_WORDS: Record<IncidentAction, { label: string; confirm: string; done: string }> = {
  fulfil: {
    label: 'Crear la reserva',
    confirm: 'Sí, crearla',
    done: 'Creada.',
  },
  refund: {
    label: 'Devolver el pago',
    confirm: 'Sí, devolver',
    done: 'Devuelto.',
  },
};

const IncidentCard = ({
  session,
  incident,
  onChanged,
}: {
  session: Session;
  incident: PaymentIncident;
  onChanged: () => void;
}): ReactElement => {
  const noteId = useId();
  const [confirming, setConfirming] = useState<IncidentAction | 'resolve' | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [note, setNote] = useState('');
  const actions = actionsFor(incident);

  const run = async (work: () => Promise<{ outcome: string }>, done: string): Promise<void> => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await work();
      setMessage({
        tone: 'ok',
        text:
          result.outcome === 'refunded' && confirming === 'fulfil'
            ? 'No quedaba sitio: el pago se devuelve solo y se avisa al cliente.'
            : done,
      });
      setConfirming(null);
      onChanged();
    } catch (err) {
      setMessage({ tone: 'error', text: errorMessage(err, 'No se pudo hacer. Prueba otra vez.') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="flex flex-col gap-2 border-b border-(color:--nuberu-line-soft) pb-4 last:border-b-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-text-primary">
          {incident.operatorName} · {subjectLabel(incident)} ·{' '}
          {formatMoney(incident.amountCents, incident.currency)}
        </p>
        <Badge tone="warning">{reasonLabel(incident)}</Badge>
      </div>
      <p className="text-sm text-text-primary">
        {incident.customerName}
        {incident.customerEmail !== '' ? ` · ${incident.customerEmail}` : ''}
        {incident.customerPhone !== null ? ` · ${incident.customerPhone}` : ''}
      </p>
      <p className="text-xs text-text-secondary">
        {incident.processor === 'stripe' ? 'Stripe' : 'Redsys'} ·{' '}
        <code className="font-mono">{incident.paymentIntentId}</code> · detectado{' '}
        {formatWhen(incident.detectedAt)} · {incident.alertCount}{' '}
        {incident.alertCount === 1 ? 'aviso' : 'avisos'}
        {incident.detail !== null ? ` · ${incident.detail}` : ''}
      </p>

      {confirming === 'fulfil' || confirming === 'refund' ? (
        <div className="flex flex-col gap-2 rounded-(--nuberu-radius-panel) bg-surface-2 p-3">
          <p className="text-sm text-text-primary">
            {confirming === 'fulfil'
              ? fulfilHint(incident)
              : `Devolvemos ${formatMoney(incident.amountCents, incident.currency)} por ${
                  incident.processor === 'stripe' ? 'Stripe' : 'Redsys'
                }. Ya no se podrá crear la reserva con este pago.`}
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={busy}
              onClick={() =>
                void run(
                  () => actOnIncident(session, incident.id, confirming),
                  ACTION_WORDS[confirming].done,
                )
              }
            >
              {busy ? '…' : ACTION_WORDS[confirming].confirm}
            </Button>
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirming(null)}>
              Cancelar
            </Button>
          </div>
        </div>
      ) : confirming === 'resolve' ? (
        <div className="flex flex-col gap-2 rounded-(--nuberu-radius-panel) bg-surface-2 p-3">
          <Label htmlFor={noteId}>Qué se hizo (queda en el registro)</Label>
          <Input
            id={noteId}
            value={note}
            maxLength={500}
            placeholder="Devuelto desde el panel de Stripe (re_…)"
            onChange={(event) => setNote(event.target.value)}
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={busy || note.trim().length < 3}
              onClick={() =>
                void run(() => resolveIncident(session, incident.id, note.trim()), 'Cerrada.')
              }
            >
              {busy ? '…' : 'Marcar resuelto'}
            </Button>
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirming(null)}>
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {actions.fulfil ? (
            <Button variant="neutral" size="sm" onClick={() => setConfirming('fulfil')}>
              {ACTION_WORDS.fulfil.label}
            </Button>
          ) : null}
          {actions.refund ? (
            <Button variant="neutral" size="sm" onClick={() => setConfirming('refund')}>
              {ACTION_WORDS.refund.label}
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" onClick={() => setConfirming('resolve')}>
            Marcar resuelto
          </Button>
        </div>
      )}
      {incident.reason === 'refund_failed' ? (
        <p className="text-xs text-text-secondary">
          Reintenta la devolución en «Colas», o devuelve desde el panel del procesador y márcalo
          resuelto.
        </p>
      ) : null}
      {message !== null ? (
        <p
          className={`text-sm ${message.tone === 'ok' ? 'text-status-success' : 'text-status-warning'}`}
          role={message.tone === 'ok' ? 'status' : 'alert'}
        >
          {message.text}
        </p>
      ) : null}
    </li>
  );
};

export const PaymentsPanel = ({ session }: { session: Session }): ReactElement => {
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  const load = useCallback(
    (signal?: AbortSignal): void => {
      void fetchPaymentIncidents(session, signal)
        .then((data) => {
          if (!signal?.aborted) setState({ status: 'ready', ...data });
        })
        .catch((err: unknown) => {
          if (signal?.aborted) return;
          setState({
            status: 'error',
            message: errorMessage(err, 'No pudimos cargar los pagos sin reserva.'),
          });
        });
    },
    [session],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Pagos sin reserva</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => load()}>
            Actualizar
          </Button>
        </CardHeader>
        <CardContent>
          {state.status === 'loading' ? (
            <p className="py-4 text-sm text-text-secondary">Cargando…</p>
          ) : state.status === 'error' ? (
            <p className="py-4 text-sm text-status-warning" role="alert">
              {state.message}
            </p>
          ) : state.open.length === 0 ? (
            <p className="py-2 text-sm text-text-secondary">Nada pendiente. 🎉</p>
          ) : (
            <ul className="flex flex-col gap-4">
              {state.open.map((incident) => (
                <IncidentCard
                  key={incident.id}
                  session={session}
                  incident={incident}
                  onChanged={() => load()}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {state.status === 'ready' && state.resolved.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Resueltos (14 días)</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="flex flex-col gap-2">
              {state.resolved.map((incident) => (
                <li
                  key={incident.id}
                  className="flex flex-col border-b border-(color:--nuberu-line-soft) pb-2 last:border-b-0"
                >
                  <p className="text-sm text-text-primary">
                    {incident.operatorName} · {incident.customerName} ·{' '}
                    {formatMoney(incident.amountCents, incident.currency)} ·{' '}
                    {resolutionLabel(incident)}
                  </p>
                  <p className="text-xs text-text-secondary">
                    {incident.resolvedAt !== null ? formatWhen(incident.resolvedAt) : ''} ·{' '}
                    {incident.resolvedBy ?? ''}
                    {incident.resolutionNote !== null ? ` · ${incident.resolutionNote}` : ''}
                  </p>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};
